/**
 * F-170 B3.5 — specialist authors their own service-area polygon.
 *
 * Loads the first existing ServiceArea from `/specialists/me/service-areas/`
 * (or hydrates a blank `service_area` zone centred on Lisboa for greenfield
 * profiles). On save we PUT the single area back through the bulk replace
 * endpoint — the backend supports multiple areas, but the MVP UI exposes
 * just one to keep the form a single canvas.
 */
import { useEffect, useState } from 'react';
import { Layout } from '@/components/layout';
import { Button, Card, ErrorState, Input, Spinner } from '@/components/atoms';
import { ServiceAreaMap } from '@/components/atoms/ServiceAreaMap';
import { useBackButton, useMainButton } from '@/hooks';
import { specialists as spApi } from '@/api';
import type { ServiceArea } from '@/types/domain';
import { toast } from '@/components/atoms/Toast';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

const BLANK: ServiceArea = {
  id: 0,
  kind: 'service_area',
  center_lat: 38.7223,
  center_lng: -9.1393,
  polygon_geojson: null,
  radius_km: null,
  address_text: '',
};

export function MyServiceAreaPage(): JSX.Element {
  useBackButton();
  const [area, setArea] = useState<ServiceArea | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setArea(null);
    (async () => {
      try {
        const data = await spApi.fetchMyServiceAreas();
        if (cancelled) return;
        const first = data.results?.[0];
        setArea(first ? first : { ...BLANK });
      } catch (err) {
        logger.warn('service areas load failed', err);
        if (!cancelled) {
          // F-180 C1: surface load failures so the specialist isn't
          // silently dropped into a blank-canvas form for an area the
          // backend may already have on file.
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const save = async (): Promise<void> => {
    if (!area) return;
    setSaving(true);
    try {
      await spApi.setMyServiceAreas([
        {
          kind: area.kind,
          center_lat: area.center_lat,
          center_lng: area.center_lng,
          polygon_geojson: area.polygon_geojson,
          radius_km: area.radius_km,
          address_text: area.address_text,
        },
      ]);
      toast.success('Збережено');
    } catch (err) {
      logger.error('service area save failed', err);
      toast.error('Не вдалось зберегти');
    } finally {
      setSaving(false);
    }
  };

  useMainButton(area ? { text: 'Зберегти', onClick: save } : null);

  return (
    <Layout title="Зона обслуговування">
      {error ? (
        <ErrorState onRetry={() => setReloadKey((k) => k + 1)} />
      ) : area === null ? (
        <Spinner center />
      ) : (
        <div className={styles.formStack}>
          <Card>
            <p className={styles.note}>
              Малюй полігон інструментом полігон, перетягни маркер у центр
              своєї зони. Клієнти бачитимуть цю карту у твоєму профілі.
            </p>
            <div style={{ marginTop: 12 }}>
              <ServiceAreaMap mode="edit" value={area} onChange={setArea} />
            </div>
          </Card>
          <Card>
            <Input
              label="Адреса (необов'язково)"
              value={area.address_text}
              onChange={(e) =>
                setArea({ ...area, address_text: e.target.value.slice(0, 255) })
              }
              maxLength={255}
              placeholder="Lisboa, Alfama"
            />
          </Card>
          <Button onClick={save} fullWidth disabled={saving}>
            {saving ? 'Збереження…' : 'Зберегти'}
          </Button>
        </div>
      )}
    </Layout>
  );
}
